import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Section, Container, Eyebrow, DisplayH1, DisplayH2, Reveal } from "@/components/Layout";
import { api } from "@/lib/api";
import { Heart, MapPin, ArrowLeft, Calendar } from "lucide-react";

const AMOUNTS = [25, 75, 250, 500];

export default function SponsorDogDetail() {
  const { id } = useParams();
  const [dog, setDog] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", amount: 75, cadence: "monthly", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/foundation/dogs/${id}`).then((r) => setDog(r.data)).catch(() => setDog(false));
  }, [id]);

  if (dog === false) return (
    <Section className="pt-40 pb-40"><Container>
      <DisplayH1>Dog not found.</DisplayH1>
      <Link to="/foundation/dogs" className="btn-ghost mt-6">Back to the pack</Link>
    </Container></Section>
  );
  if (!dog) return <Section className="pt-40 pb-40"><Container><div className="text-[color:var(--wk-bone-dim)]">Loading…</div></Container></Section>;

  const pct = Math.min(100, Math.round((dog.raised_usd / dog.goal_usd) * 100));
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/foundation/dogs/${dog.dog_id}/sponsor`, {
        ...form, dog_id: dog.dog_id, amount: Number(form.amount),
      });
      toast.success(`Thank you. You'll receive ${dog.name}'s next update by email.`);
      setForm({ ...form, name: "", email: "", message: "" });
      // refresh totals
      const r = await api.get(`/foundation/dogs/${dog.dog_id}`);
      setDog(r.data);
    } catch { toast.error("Could not record your sponsorship."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Section testid="dog-hero" className="pt-32 pb-6">
        <Container>
          <Link to="/foundation/dogs" className="link-underline text-sm text-[color:var(--wk-bone-dim)] inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to the pack
          </Link>
          <div className="mt-6 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="relative overflow-hidden rounded-3xl aspect-[4/3]">
                <img src={dog.image} alt={dog.name} className="w-full h-full object-cover kenburns" />
                <div className="absolute inset-0 cinema-overlay" />
                <div className="absolute top-4 left-4 overline text-[10px] px-3 py-1 rounded-full glass border-white/20">{dog.stage}</div>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {dog.gallery?.map((g, i) => (
                  <div key={i} className="relative aspect-square overflow-hidden rounded-xl">
                    <img src={g} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <Eyebrow>Golden Wolf Foundation</Eyebrow>
              <DisplayH1 className="mt-4">{dog.name}</DisplayH1>
              <div className="mt-2 text-[color:var(--wk-bone-dim)] flex items-center gap-2">
                <span>{dog.breed} · {dog.age_months} months</span>
                <span>·</span>
                <MapPin size={12} /> {dog.location}
              </div>
              <p className="mt-6 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">{dog.story}</p>

              <div className="mt-8 card-lux p-6">
                <div className="flex justify-between text-sm">
                  <div className="text-[color:var(--wk-bone)]">${dog.raised_usd.toLocaleString()}</div>
                  <div className="text-[color:var(--wk-bone-dim)]">of ${dog.goal_usd.toLocaleString()}</div>
                </div>
                <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-[color:var(--wk-gold)]" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-4 flex items-center gap-4 text-xs text-[color:var(--wk-bone-dim)]">
                  <span className="flex items-center gap-1"><Heart size={12} className="text-[color:var(--wk-gold)]" /> {dog.sponsors} sponsors</span>
                  <span>{pct}% funded</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Milestones */}
      <Section testid="dog-milestones" className="py-16 border-t border-white/10 mt-16">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Eyebrow>The Journey</Eyebrow>
              <DisplayH2 className="mt-3">{dog.name}'s milestones.</DisplayH2>
            </div>
            <div className="lg:col-span-7 space-y-4">
              {dog.milestones?.map((m, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="card-lux p-6 flex gap-6">
                    <Calendar className="text-[color:var(--wk-gold)] shrink-0 mt-1" size={20} />
                    <div>
                      <div className="overline">{m.month}</div>
                      <p className="mt-2 text-[color:var(--wk-bone)] leading-relaxed">{m.note}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Sponsor form */}
      <Section testid="dog-sponsor" className="py-16 border-t border-white/10">
        <Container>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="card-lux p-8 md:p-12 max-w-3xl">
            <Eyebrow>Sponsor {dog.name}</Eyebrow>
            <div className="mt-3 font-serif-display text-3xl">Follow a life. Fund a story.</div>
            <form onSubmit={submit} data-testid="sponsor-form" className="mt-8 space-y-6">
              <div className="flex flex-wrap gap-3">
                {AMOUNTS.map((a) => (
                  <button
                    type="button"
                    key={a}
                    onClick={() => set("amount", a)}
                    data-testid={`sponsor-amount-${a}`}
                    className={`px-6 py-3 rounded-full text-sm border ${form.amount === a ? "bg-[color:var(--wk-gold)] text-black border-[color:var(--wk-gold)]" : "border-white/20 hover:border-[color:var(--wk-gold)]"}`}
                  >
                    ${a.toLocaleString()}
                  </button>
                ))}
                <input type="number" min="1" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="w-32 bg-transparent border border-white/20 rounded-full px-4 py-3 text-sm outline-none focus:border-[color:var(--wk-gold)]" />
              </div>
              <div className="flex gap-3">
                {["monthly", "one-time"].map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => set("cadence", c)}
                    className={`px-4 py-2 rounded-full text-xs capitalize border ${form.cadence === c ? "bg-[color:var(--wk-gold)] text-black border-[color:var(--wk-gold)]" : "border-white/20 hover:border-[color:var(--wk-gold)]"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="overline">Full Name</label>
                  <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-lg" data-testid="sponsor-name" />
                </div>
                <div>
                  <label className="overline">Email</label>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-lg" data-testid="sponsor-email" />
                </div>
              </div>
              <div>
                <label className="overline">A message to {dog.name}'s trainer (optional)</label>
                <textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3 text-base resize-none" />
              </div>
              <div className="text-xs text-[color:var(--wk-bone-dim)]">Note: pledge only — payment processing activates in the next phase.</div>
              <button disabled={busy} className="btn-gold" data-testid="sponsor-submit">{busy ? "Sending love…" : `Sponsor ${dog.name}`}</button>
            </form>
          </motion.div>
        </Container>
      </Section>
    </>
  );
}
